/**
 * gRPC-Web Protocol Transport Configuration
 * 
 * This file provides the unified transport layer for all gRPC service calls
 * in the Guardian AI frontend.
 * 
 * Using gRPC-Web protocol for compatibility with tonic-web backend.
 * gRPC-Web: https://github.com/grpc/grpc-web
 */

import { createGrpcWebTransport } from '@connectrpc/connect-web';
import type { Transport, Interceptor } from '@connectrpc/connect';

// =========================================================================
// ENVIRONMENT CONFIGURATION
// =========================================================================

// Determine if we are running in the browser
const isBrowser = typeof window !== 'undefined';

// Base URL for API requests
// In production, we assume the frontend and backend are served from the same domain
// or configured via environment variables. For local dev, we default to localhost.
export const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:8080');

// Note: Cloud Run ingress routes /grpc/* to the Envoy sidecar container (via backend_host env in production config)
export const GRPC_BASE_URL = import.meta.env.VITE_GRPC_URL || (import.meta.env.PROD ? '/grpc' : 'http://localhost:50051');

// =============================================================================
// PRODUCTION GUARD — fail fast if misconfigured
// =============================================================================
// If localhost sneaks past the Dockerfile build guard, this throws immediately
// in production (before any API call), making the misconfiguration obvious
// rather than silently failing all gRPC requests.
if (import.meta.env.PROD && GRPC_BASE_URL.includes('localhost')) {
    throw new Error(
        `[Guardian AI] FATAL: GRPC_BASE_URL is set to "${GRPC_BASE_URL}" in a production build. ` +
        `This means VITE_GRPC_URL was not set correctly during the Docker build. ` +
        `Expected value: "/grpc". Check the Dockerfile ARG VITE_GRPC_URL.`
    );
}

// =========================================================================
// AUTHENTICATION INTERCEPTOR
// =========================================================================

/**
 * Authentication interceptor for gRPC-Web requests.
 * Automatically injects the JWT token as a Bearer token in the Authorization header.
 */
const authInterceptor: Interceptor = (next) => async (req) => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (token) {
        req.header.set('Authorization', `Bearer ${token}`);
    }

    // Support for Impersonation
    const impersonatedUserStr = sessionStorage.getItem('impersonatedUser');
    if (impersonatedUserStr) {
        try {
            const user = JSON.parse(impersonatedUserStr);
            if (user && user.id) {
                req.header.set('x-impersonate-user-id', user.id);
            }
        } catch (e) {
            console.error('Failed to parse impersonated user', e);
        }
    }

    return next(req);
};

/**
 * Logging interceptor for development debugging
 */
const loggingInterceptor: Interceptor = (next) => async (req) => {
    const startTime = performance.now();
    const serviceName = req.service.typeName;
    const methodName = req.method.name;

    if (import.meta.env.DEV) {
        console.log(`[gRPC-Web] → ${serviceName}.${methodName}`, req.message);
    }

    try {
        const response = await next(req);
        const duration = Math.round(performance.now() - startTime);

        if (import.meta.env.DEV) {
            console.log(`[gRPC-Web] ✓ ${serviceName}.${methodName} (${duration}ms)`, response.message);
        }

        return response;
    } catch (error) {
        const duration = Math.round(performance.now() - startTime);
        if (import.meta.env.DEV) {
            console.error(`[gRPC-Web] ✗ ${serviceName}.${methodName} (${duration}ms)`, error);
        }
        throw error;
    }
};

/**
 * Error handling interceptor that normalizes gRPC errors
 */
const errorInterceptor: Interceptor = (next) => async (req) => {
    try {
        return await next(req);
    } catch (error: unknown) {
        // Handle authentication errors (401/403)
        if (error && typeof error === 'object' && 'code' in error) {
            const grpcError = error as { code: number; message?: string };
            if (grpcError.code === 16) { // UNAUTHENTICATED
                const isImpersonating = !!sessionStorage.getItem('impersonatedUser');
                // If impersonating, the unauthenticated error is due to role mismatch, not token expiration.
                if (!isImpersonating) {
                    // Clear invalid token and trigger re-auth
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                    window.dispatchEvent(new CustomEvent('auth:expired'));
                } else {
                    console.warn('[gRPC-Web] UNAUTHENTICATED error ignored during impersonation block. It is a role mismatch.');
                }
            }
        }
        throw error;
    }
};

// =============================================================================
// Transport
// =============================================================================

/**
 * The unified gRPC-Web transport for all service calls
 * 
 * Uses gRPC-Web protocol for compatibility with tonic-web backend.
 */
export const transport: Transport = createGrpcWebTransport({
    baseUrl: GRPC_BASE_URL,
    useBinaryFormat: true,
    interceptors: [
        authInterceptor,
        loggingInterceptor,
        errorInterceptor,
    ],
});

/**
 * Create a transport with custom options (useful for testing)
 */
function createTransport(options?: {
    baseUrl?: string;
    useBinaryFormat?: boolean;
    interceptors?: Interceptor[];
}): Transport {
    return createGrpcWebTransport({
        baseUrl: options?.baseUrl ?? GRPC_BASE_URL,
        useBinaryFormat: options?.useBinaryFormat ?? true,
        interceptors: options?.interceptors ?? [
            authInterceptor,
            loggingInterceptor,
            errorInterceptor,
        ],
    });
}

;
